import {
    StreamRequestInfo,
    WidgetCustomManagedRestOptions,
    WidgetCustomStreamManager,
    WidgetRestByRequestUidConfig,
} from 'widgets/streaming/widgetStreamManager/WidgetCustomStreamManager';
import State from 'store/State';
import RequestProgressService from 'requestProgress/RequestProgressService';
import IndigoServiceClient from 'communication/IndigoServiceClient';
import { ActionsDispatcher } from 'store/ActionsDispatcher';
import IndigoApiError from 'communication/IndigoApiError';
import { WidgetRequestResponseByUidRestManager } from 'widgets/streaming/widgetStreamManager/RequestResponse/WidgetRequestResponseByUidRestManager';
import { selectPortfolioSelection } from 'portfolio/store/selectors';
import { PortfolioSelectionWrapper } from 'portfolio/PortfolioSelectionWrapper';

import {
    addError,
    addRequest,
    addResponse,
    clearData,
} from './store/data/dataSlice';

import {
    DarkVenueHeatmapRequest,
    toUid,
} from 'widgets/darkVenueHeatmap/store/data/DarkVenueHeatmapRequest';
import { DarkVenueHeatmapApiResponse } from './api/DarkVenueHeatmapApiResponse';
import { callDarkVenueHeatmapApi } from './api/DarkVenueHeatmapApi';
import { toApiRequest } from './store/data/mapping.ts';
import { SymbolsService } from 'symbols/SymbolsService.ts';
import { DataServices } from 'store/Services.ts';

export const darkVenueHeatmapStreamConfig: WidgetRestByRequestUidConfig<
    DarkVenueHeatmapRequest,
    DarkVenueHeatmapApiResponse
> = {
    apiUid: 'DarkVenueHeatmap',
    managerFactory(
        options: WidgetCustomManagedRestOptions
    ): WidgetCustomStreamManager {
        return new WidgetRequestResponseByUidRestManager(options);
    },
    getWidgetStreamRequestInfo(
        state: State
    ): Promise<
        | StreamRequestInfo<DarkVenueHeatmapRequest>[]
        | StreamRequestInfo<DarkVenueHeatmapRequest>
        | undefined
    > {
        return Promise.resolve(getDarkVenueHeatmapStreamRequestInfo(state));
    },
    getStreamRequestInfosForSymbols(): Promise<
        StreamRequestInfo<DarkVenueHeatmapRequest>[]
    > {
        return Promise.resolve([]);
    },
    callApi(
        dispatcher: ActionsDispatcher,
        requestProgressService: RequestProgressService,
        indigoServiceClient: IndigoServiceClient,
        dataServices: DataServices,
        payload: DarkVenueHeatmapRequest
    ): Promise<DarkVenueHeatmapApiResponse> {
        return callApi(
            dispatcher,
            requestProgressService,
            indigoServiceClient,
            dataServices.symbolsService,
            payload
        );
    },
    clearRequestData(): void {
        return;
    },
    handleMessage(
        dispatcher: ActionsDispatcher,
        key: string,
        request: DarkVenueHeatmapRequest,
        response: DarkVenueHeatmapApiResponse
    ): void {
        dispatcher.dispatch(addResponse({ request, response }));
    },
    handleError(
        dispatcher: ActionsDispatcher,
        key: string,
        request: DarkVenueHeatmapRequest,
        error: IndigoApiError
    ): void {
        dispatcher.dispatch(addError({ request, error }));
    },
    removeRequestData(dispatcher: ActionsDispatcher, key: string): void {
        dispatcher.dispatch(clearData(key));
    },
};

function getDarkVenueHeatmapStreamRequestInfo(
    state: State
): StreamRequestInfo<DarkVenueHeatmapRequest> | undefined {
    const selection = selectPortfolioSelection(state);
    const selectionWrapper = new PortfolioSelectionWrapper(selection);

    const symbol = selectionWrapper.selectedSymbol;
    const symbology = selectionWrapper.symbology;

    if (!symbol) {
        return undefined;
    }

    const request = { symbol, symbology };

    const uid = toUid(request);

    return {
        streamKey: uid,
        payloadUid: uid,
        payload: request,
    };
}

async function callApi(
    dispatcher: ActionsDispatcher,
    requestProgressService: RequestProgressService,
    indigoServiceClient: IndigoServiceClient,
    symbolsService: SymbolsService,
    payload: DarkVenueHeatmapRequest
) {
    const apiRequest = await toApiRequest(payload, symbolsService);

    dispatcher.dispatch(addRequest({ request: payload, apiRequest }));
    dispatcher.dispose(); // Force delayed dispatch...

    return requestProgressService.invokeAndMonitorRequest(
        'callDarkVenueApi',
        (options) =>
            callDarkVenueHeatmapApi(indigoServiceClient, apiRequest, options)
    );
}
